import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabaseClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditor, setIsEditor] = useState(false);
  const isRefreshingRef = useRef(false);

  const refreshUserSession = async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error("Error refreshing session:", error);
      return { data: null, error };
    }
  };

  useEffect(() => {
    let mounted = true;

    const handleSession = (currentSession) => {
      if (!mounted) return;

      if (currentSession?.user) {
        const userRole = currentSession.user.app_metadata?.role;
        
        // If role is missing, trigger an automatic refresh to fetch newly minted claims
        if (!userRole && !isRefreshingRef.current) {
          isRefreshingRef.current = true;
          // Attempt refresh async without blocking current render
          supabase.auth.refreshSession().catch(console.error);
        }

        // Strict Zero Trust: fallback to 'student' if missing or unrecognized
        const safeRole = userRole || 'student';

        setIsAdmin(safeRole === 'admin');
        setIsEditor(safeRole === 'writer' || safeRole === 'admin');
      } else {
        setIsAdmin(false);
        setIsEditor(false);
        isRefreshingRef.current = false;
      }

      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);
    };

    // 🛡️ Recupero sessione con protezione anti-crash globale
    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        if (error) throw error;
        handleSession(session);
      })
      .catch((err) => {
        console.error("Session crash:", err);
        if (mounted) setLoading(false);
      });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (_event === 'SIGNED_OUT') {
           isRefreshingRef.current = false;
        }
        handleSession(session);
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email, password) => {
    return await supabase.auth.signUp({ email, password });
  };

  const signIn = async (email, password) => {
    return await supabase.auth.signInWithPassword({ email, password });
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  // 🚀 LOGIN GOOGLE — COMPATIBILE iOS/SAFARI
  // Su iOS, i popup OAuth vengono bloccati. Usiamo sempre il redirect.
  // Il flow PKCE (impostato in supabaseClient) gestisce il ritorno in modo sicuro
  // anche quando Safari "dimentica" lo stato della sessione.
  const signInWithGoogle = async () => {
    try {
      // 🍎 iOS Safari blocca i redirect avviati in modo asincrono (es. dopo un await).
      // Per questo motivo non facciamo nessun await prima di chiamare signInWithOAuth:
      // la chiamata deve avvenire il più sincrona possibile rispetto al tap dell'utente.
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
          // 🚀 skipBrowserRedirect: false assicura che il redirect avvenga
          // immediatamente nel tab corrente, senza aprire popup (bloccati da iOS).
          skipBrowserRedirect: false,
        }
      });
      
      if (error) throw error;
      return { data, error: null };
      
    } catch (error) {
      console.error("Google SignIn Error:", error);
      // 🚨 Se qualcosa va storto, il paziente "parla" invece di svenire
      alert("Blocco di sistema! Errore esatto: " + error.message);
      return { data: null, error };
    }
  };

  const resetPassword = async (email) => {
    return await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
  };

  return (
    <AuthContext.Provider value={{
      session, user, loading, isAdmin, isEditor, signUp, signIn, signOut, signInWithGoogle, resetPassword, refreshUserSession
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};

export const getAuthHeaders = () => {
  if (typeof window === 'undefined') return {};
  // Try to get session from Supabase localStorage
  const supabaseKeys = Object.keys(localStorage).filter(k => k.includes('supabase'));
  for (const key of supabaseKeys) {
    try {
      const data = JSON.parse(localStorage.getItem(key));
      if (data?.access_token) {
        return { Authorization: `Bearer ${data.access_token}` };
      }
    } catch {}
  }
  return {};
};
